import { useState, type PropsWithChildren } from 'react';
import { useLocation } from 'react-router';
import { LockKeyhole } from 'lucide-react';
import { Button } from '@accio3d/design-system/button';
import PageLoadingState from '@accio3d/shared-ui/components/ui/page-loading-state';
import { useSupplierTerms } from '../../hooks/useSupplierTerms';
import { SupplierTermsContext, useSupplierTermsAccess } from '../../hooks/useSupplierTermsAccess';
import { useAuth } from '../../hooks/useAuth';
import { SupplierWelcomeModal } from './supplier-welcome-modal';
import { SupplierNextStepsModal } from './supplier-next-steps-modal';

/** Shares agreement state and the welcome dialog across the supplier app. */
export function SupplierTermsProvider({ children }: PropsWithChildren) {
  const { organization } = useAuth();
  const terms = useSupplierTerms();
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [nextStepsFor, setNextStepsFor] = useState<string | null>(null);
  const key = `${organization?.id}:${terms.data?.currentUrl}`;
  const needsAcceptance = terms.isSupplier && terms.isSuccess && !terms.hasAccepted;
  return (
    <SupplierTermsContext.Provider value={{ terms, openWelcome: () => setDismissed(null) }}>
      {children}
      {needsAcceptance && terms.data && (
        <SupplierWelcomeModal
          key={key}
          open={dismissed !== key}
          onOpenChange={(open) => setDismissed(open ? null : key)}
          onAccepted={() => setNextStepsFor(key)}
          url={terms.data.currentUrl}
        />
      )}
      {terms.isSupplier && terms.hasAccepted && nextStepsFor === key && (
        <SupplierNextStepsModal open onOpenChange={() => setNextStepsFor(null)} />
      )}
    </SupplierTermsContext.Provider>
  );
}

/** Masks opportunity content without loading confidential RFQ rows. */
export function SupplierTermsGate() {
  const { terms, openWelcome } = useSupplierTermsAccess();
  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Manufacturing opportunities</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Your next opportunity starts with Arcessio.
        </p>
      </div>
      <div className="relative isolate min-h-96 overflow-hidden rounded-xl border bg-card">
        <div aria-hidden="true" className="pointer-events-none select-none p-6 opacity-45 blur-sm">
          <div className="mb-7 flex gap-3">
            {[0, 1, 2].map((n) => (
              <div key={n} className="h-20 flex-1 rounded-lg bg-muted" />
            ))}
          </div>
          <div className="grid grid-cols-4 gap-4 border-b pb-4 text-xs font-medium text-muted-foreground">
            <span>Part name</span>
            <span>Process</span>
            <span>Material</span>
            <span>Status</span>
          </div>
          {[0, 1, 2, 3, 4].map((n) => (
            <div key={n} className="grid grid-cols-4 gap-4 border-b py-5">
              {[0, 1, 2, 3].map((cell) => (
                <div key={cell} className="h-3 w-3/4 rounded bg-muted-foreground/20" />
              ))}
            </div>
          ))}
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-background/35 p-5">
          <div className="w-full max-w-sm rounded-2xl border bg-background p-7 text-center shadow-xl">
            <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <LockKeyhole className="h-5 w-5" />
            </span>
            <h2 className="text-lg font-semibold">
              {terms.isPending
                ? 'Checking agreement…'
                : terms.isError
                  ? 'Unable to check your agreement'
                  : 'Unlock your RFQs'}
            </h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {terms.isError
                ? 'Please try again to check your organization’s acceptance.'
                : 'Agree to our Terms of Use to view RFQs and explore your manufacturing opportunities.'}
            </p>
            {terms.isError ? (
              <Button className="mt-5 w-full" onClick={() => void terms.refetch()}>
                Try again
              </Button>
            ) : (
              <Button className="mt-5 w-full" onClick={openWelcome} disabled={terms.isPending}>
                Review and agree to terms
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/** Keeps RFQs and their related screens locked until acceptance is confirmed. */
export function SupplierTermsRouteGate({ children }: PropsWithChildren) {
  const { pathname } = useLocation();
  const { terms } = useSupplierTermsAccess();
  const requiresTerms =
    pathname === '/' ||
    ['/rfqs', '/messages', '/quotes', '/parts'].some(
      (path) => pathname === path || pathname.startsWith(`${path}/`),
    );

  if (requiresTerms && terms.isSupplier && terms.isPending) return <PageLoadingState />;
  return requiresTerms && !terms.hasAccepted ? <SupplierTermsGate /> : children;
}
